<template>
  <div class="margin-bottom">
    <widget-header>{{ config.name || 'Overview' }}</widget-header>
    <div class="responsive-grid">
      <div class="responsive-grid__item" data-test="performance-questions-answered">
        <performance-overall-item
          icon-name="check-circle-uui"
          title="Questions Answered"
          :value="String(questionsAnswered)"
        />
      </div>
      <div class="responsive-grid__item" data-test="performance-percent-correct">
        <performance-overall-item
          icon-name="pie-chart-uui"
          title="Percent Correct"
          :value="percentCorrectlyAnswered"
        />
      </div>
      <div class="responsive-grid__item" data-test="performance-average-time">
        <performance-overall-item
          icon-name="stop-watch-uui"
          title="Avg Time per Question"
          :value="`${averageTimePerQuestion}`"
        />
      </div>
      <div class="responsive-grid__item" data-test="performance-longest-streak">
        <performance-overall-item
          icon-name="flame-uui"
          title="Longest Streak"
          :value="`${longestStreak}`"
        />
      </div>
    </div>
  </div>
</template>

<script>
import WidgetHeader from '@components/dashboard-widgets/WidgetHeader';
import PerformanceOverallItem from '@components/dashboard-widgets/performanceOverallItem';
import { useFlashcardCounts, resolveResource } from '@/composables/useFlashcardCounts';
import { formatTotalSeconds } from '@/utils';

export default {
  components: { WidgetHeader, PerformanceOverallItem },
  props: {
    config: { type: Object },
    dashboard: { type: Object },
  },
  setup() {
    const {
      getFlashcardCounts,
      collectCategoryIdsByResource,
      countUniqueAnsweredFlashcards,
      getUniqueLatestFlashcardOutcomes,
    } = useFlashcardCounts();
    return {
      getFlashcardCounts,
      collectCategoryIdsByResource,
      countUniqueAnsweredFlashcards,
      getUniqueLatestFlashcardOutcomes,
    };
  },
  computed: {
    resolvedResource() {
      const widgetConfig = this.config?.config || {};
      return resolveResource(widgetConfig);
    },
    resourceCounts() {
      return this.getFlashcardCounts(this.resolvedResource?.id, this.resolvedResource?.type);
    },
    categoryIdSet() {
      return new Set(this.collectCategoryIdsByResource(this.resolvedResource?.id, this.resolvedResource?.type));
    },
    flashcardStates() {
      return this.$store.getters['flashcardStates/flashcardStateList']
        .filter(cs => this.categoryIdSet.has(cs.categoryId));
    },
    questionsAnswered() {
      return this.countUniqueAnsweredFlashcards(this.resolvedResource?.id, this.resolvedResource?.type);
    },
    uniqueLatestOutcomes() {
      return this.getUniqueLatestFlashcardOutcomes(this.resolvedResource?.id, this.resolvedResource?.type);
    },
    percentCorrectlyAnswered() {
      const { answered, correct } = this.uniqueLatestOutcomes;
      if (answered === 0) return 'N/A';
      return Math.round((correct / answered) * 100) + '%';
    },
    averageTimePerQuestion() {
      if (this.resourceCounts.averageTime == null) return 'N/A';
      return formatTotalSeconds(this.resourceCounts.averageTime);
    },
    longestStreak() {
      const ordered = [...this.flashcardStates].sort((a, b) => a.mobileCreatedAt - b.mobileCreatedAt);
      let longest = 0;
      let current = 0;
      for (const state of ordered) {
        if (state.correct === true) {
          current++;
        } else if (state.correct === false) {
          if (current > longest) longest = current;
          current = 0;
        }
      }
      return current > longest ? current : longest;
    },
  },
};
</script>

<style lang="scss" scoped>
.responsive-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  grid-auto-rows: minmax(rem-calc(86), auto);

  .responsive-grid__item {
    background: var(--bg-primary);
    border: 1px solid var(--Colors-Border-border-secondary);
    border-radius: var(--radius-xl);
    box-sizing: border-box;
    padding: var(--spacing-xl) var(--spacing-2xl);
  }
}

@include breakpoint(tablet-up) {
  .responsive-grid {
    grid-template-columns: repeat(auto-fit, minmax(rem-calc(280), 1fr));
  }
}

@media (min-width: 1201px) {
  .margin-bottom {
    margin-bottom: 2.5rem;
  }

  .responsive-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
